import {VisuallyHidden} from '@commercial-helios/ui/src/components/visually-hidden'
import {tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import ExternalLink from '@helios/shared/components/external-link'
import LocaleSwitcher from '@helios/shared/components/locale-switcher'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import type {Props as RichTextProps} from '@helios/shared/components/rich-text'
import RichText from '@helios/shared/components/rich-text'
import logoFooterIcon from '@helios/shared/images/icons/logo-horizontal.svg'
import {socialLinks} from '@helios/shared/site-config'
import {Section} from '@helios/shared/styles/grid'
import {Text, NavTextLink} from '@helios/shared/styles/typography'
import type Locale from '@helios/shared/types/locale'
import mq from '@helios/shared/utils/media-query'
import replacePlaceholders from '@helios/shared/utils/replace-placeholders'

type FooterProps = {
    locales: Locale[]
    hideLinks?: boolean
    content: {
        entity: string
        address: RichTextProps['content']
        terms_body: string
        copyright_text: string
        financial_partner_terms_text: RichTextProps['content']
        financial_partner_copyright_text: string
        product_heading: string
        resources_heading: string
        company_heading: string
        legal_heading: string
        partnerships_heading: string
        solutions_heading: string
        product_links: Link[]
        resources_links: Link[]
        company_links: Link[]
        legal_links: Link[]
        partnerships_links: Link[]
        solutions_links: Link[]
    }
}

const currentYear = new Date().getFullYear()

export const Footer = ({content, locales, hideLinks = false}: FooterProps) => (
    <Wrapper>
        <StyledSection>
            <div>
                <Logo src={logoFooterIcon} alt="" />
                <LocaleSwitcher locales={locales} light />
            </div>

            <Links>
                {!hideLinks && (
                    <>
                        {!!content.product_links.length && (
                            <LinkGroup
                                heading={content.product_heading}
                                links={content.product_links}
                            />
                        )}
                        {!!content.company_links.length && (
                            <LinkGroup
                                heading={content.company_heading}
                                links={content.company_links}
                            />
                        )}
                        {!!content.partnerships_links.length && (
                            <LinkGroup
                                heading={content.partnerships_heading}
                                links={content.partnerships_links}
                            />
                        )}
                        {!!content.resources_links.length && (
                            <LinkGroup
                                heading={content.resources_heading}
                                links={content.resources_links}
                            />
                        )}
                        {!!content.solutions_links.length && (
                            <LinkGroup
                                heading={content.solutions_heading}
                                links={content.solutions_links}
                            />
                        )}
                        {!!content.legal_links.length && (
                            <LinkGroup
                                heading={content.legal_heading}
                                links={content.legal_links}
                            />
                        )}
                    </>
                )}
            </Links>

            <div>
                <SocialLinks>
                    {socialLinks.map((link) => (
                        <ExternalLink key={link.name} to={link.url}>
                            <SocialIcon as={link.icon} width={24} height={24} aria-hidden />

                            <VisuallyHidden>{link.name}</VisuallyHidden>
                        </ExternalLink>
                    ))}
                </SocialLinks>

                <Text as={Address} color="muted">
                    <Text variant="paragraph-base">
                        {content.entity}
                        <br />
                    </Text>

                    <RichText content={content.address} variant="paragraph-base" />
                </Text>
            </div>

            <Terms>
                <Text color="muted" variant="paragraph-base">
                    {content.terms_body}
                </Text>

                <Text color="muted" variant="paragraph-base">
                    {content.entity}, {currentYear}. {content.copyright_text}
                </Text>

                {content.financial_partner_terms_text && (
                    <RichText
                        content={content.financial_partner_terms_text}
                        color="muted"
                        variant="paragraph-base"
                    />
                )}

                {content.financial_partner_copyright_text && (
                    <Text color="muted" variant="paragraph-base">
                        {replacePlaceholders(content.financial_partner_copyright_text, {
                            '{{currentYear}}': `${currentYear}`,
                        })}
                    </Text>
                )}
            </Terms>
        </StyledSection>
    </Wrapper>
)

type Link = {
    text: string
    link: PrismicLinkProps
}

type LinkGroupProps = {
    heading: string
    links: Link[]
}

const LinkGroup = ({heading, links}: LinkGroupProps) => (
    <StyledLinkGroup aria-label={heading}>
        <Text as="h3" variant="paragraph-large" color="muted">
            {heading}
        </Text>

        {links?.map((item, i) => (
            <NavTextLink
                as={PrismicLink}
                variant="paragraph-base"
                key={i}
                color="muted"
                {...item?.link}
            >
                {item.text}
            </NavTextLink>
        ))}
    </StyledLinkGroup>
)

const Wrapper = styled.footer`
    background-color: ${tokens.shade200};
`

const StyledSection = styled(Section)`
    display: grid;
    grid-row-gap: ${tokens.spacing40};

    ${mq.min('sm')} {
        grid-column-gap: 80px;
        grid-row-gap: 80px;
        grid-template-columns: repeat(4, 1fr);
    }

    ${mq.min('md')} {
        grid-column-gap: 100px;
    }
`

const Logo = styled.img`
    width: 80px;
    margin-bottom: ${tokens.spacing20};

    ${mq.min('md')} {
        width: 90px;
        margin-bottom: ${tokens.spacing28};
    }
`

export const SocialLinks = styled.nav`
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: start;
    column-gap: ${tokens.spacing8};
    margin-bottom: ${tokens.spacing20};

    ${mq.min('sm')} {
        order: 1;
    }

    > * {
        display: flex;
    }
`

const SocialIcon = styled.div`
    color: ${tokens.shade700};
    transition: ${tokens.fastIn};
    transition-property: color;

    a:hover & {
        color: ${tokens.shade900};
    }
`

const Links = styled.div`
    display: grid;
    grid-row-gap: ${tokens.spacing40};
    grid-column: 2 / -1;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-column-gap: ${tokens.spacing40};
        grid-template-columns: repeat(3, 1fr);
    }

    ${mq.min('md')} {
        grid-column-gap: 80px;
    }
`

const Terms = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-column: 2 / -1;
        height: fit-content;
    }
`

const Address = styled.div`
    ${mq.min('sm')} {
        order: 2;
    }
`

const StyledLinkGroup = styled.nav`
    display: grid;
    grid-template-columns: auto;
    row-gap: ${tokens.spacing8};
    align-content: start;
    justify-items: start;
`
